import { useEffect, useMemo, useState } from "react";
import { useForkRef } from "@mui/material/utils";

import {
  getArrowSkidding,
  getPanelOffset,
  getPopperModifiers,
  isOverlayOwnedByPanel,
} from "ui/popover/popover.helpers";
import { type PopoverProps } from "ui/popover/popover.types";

type UsePopoverProps = Pick<
  PopoverProps,
  "open" | "onOpen" | "onClose" | "keepPageInteractive" | "ref" | "anchorEl" | "disableAutoFocus"
> &
  Required<Pick<PopoverProps, "arrow" | "gutter">> &
  Partial<Pick<PopoverProps, "placement">>;

export const usePopover = ({
  open: controlledOpen,
  onOpen,
  onClose,
  arrow,
  gutter,
  keepPageInteractive,
  ref,
  anchorEl: externalAnchorEl,
  disableAutoFocus = false,
  placement = "bottom",
}: UsePopoverProps) => {
  const [triggerEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const anchorEl = externalAnchorEl === undefined ? triggerEl : externalAnchorEl;
  const [internalOpen, setInternalOpen] = useState(false);
  const [paperEl, setPaperEl] = useState<HTMLDivElement | null>(null);

  const paperRef = useForkRef(setPaperEl, ref);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleTriggerClick = () => {
    if (internalOpen) {
      setInternalOpen(false);
      onClose?.();
    } else {
      setInternalOpen(true);
      onOpen?.();
    }
  };

  const handleClose = () => {
    if (!isControlled) {
      setInternalOpen(false);
    }
    onClose?.();
  };

  const offset = getPanelOffset(gutter, arrow);

  const popperModifiers = useMemo(() => getPopperModifiers(offset, arrow), [offset, arrow]);

  /**
   * The `Popover` path's skidding is a margin, so it reads the trigger's size once per render; the
   * `Popper` path reads it live in its modifier.
   */
  const skidding =
    arrow && anchorEl
      ? getArrowSkidding(placement, { width: anchorEl.offsetWidth, height: anchorEl.offsetHeight })
      : 0;

  useEffect(() => {
    /**
     * `Modal` moved focus into the panel on open and put it back on close; the `Popper` path has
     * to do both itself. Without it the trigger keeps focus while the panel sits at the end of
     * `document.body`, so Tab walks the rest of the page instead of entering the panel.
     *
     * Deliberately apart from the dismissal effect, which re-subscribes on every render —
     * refocusing at that rate would fight a user typing inside the panel.
     */
    if (!keepPageInteractive || disableAutoFocus || !open || !paperEl) return;

    const previouslyFocused = document.activeElement;
    paperEl.focus();

    return () => {
      /**
       * Focus is ours to hand back only if the user has not already moved it: the Paper still
       * holds it while the exit transition runs, and `body` is where it falls once the Paper
       * unmounts.
       *
       * `preventScroll` because closing is not always the user's doing — `CommentPopover` closes
       * itself once `useOnScreen` says the pin has scrolled away, which only became reachable now
       * that the page scrolls. A plain `focus()` would drag the viewport back to that offscreen
       * trigger and undo the scroll this prop exists to allow.
       */
      const active = document.activeElement;
      const isFocusStillOurs = active === document.body || paperEl.contains(active);

      if (isFocusStillOurs && previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [keepPageInteractive, disableAutoFocus, open, paperEl]);

  useEffect(() => {
    /** Both behaviours came from `Modal`, which the non-modal path does not render. */
    if (!keepPageInteractive || !open) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (paperEl?.contains(target) || anchorEl?.contains(target)) return;
      if (isOverlayOwnedByPanel(paperEl, target)) return;
      handleClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [keepPageInteractive, open, anchorEl, paperEl, handleClose]);

  return {
    open,
    anchorEl,
    setAnchorEl,
    paperRef,
    handleClose,
    offset,
    skidding,
    /** A controlled popover leaves the trigger's own `onClick` in charge. */
    handleTriggerClick: isControlled ? undefined : handleTriggerClick,
    popperModifiers,
  };
};
